import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneIcon from '@mui/icons-material/Done';
import { Box, IconButton, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import CardContentEditable from 'components/StudiesDashboard/CardContentEditable';
import { map } from 'es-toolkit/compat';
import { ReportSection } from 'interfaces/agentSession';
import Markdown from 'markdown-to-jsx';
import React, { useCallback } from 'react';
import { useClipboard } from 'use-clipboard-copy';

import SlideLinkWithPreview from 'components/atoms/SlideLinkWithPreview';
import { useEntityMergeTagResolver } from 'utils/useEntityMergeTagResolver';
import MermaidDiagram from './MermaidDiagram';
import StudyReportSectionChartCard from './StudyReportSectionChartCard';
import StudyReportSectionSurface from './StudyReportSectionSurface';
import StudyReportSectionTableCard from './StudyReportSectionTableCard';

// Markdown renderer overrides to enforce safe external-link behavior.
const sectionMarkdownOptions = {
  overrides: {
    a: {
      component: Link,
      props: { target: '_blank', rel: 'noopener noreferrer', underline: 'hover' },
    },
    'slide-link': { component: SlideLinkWithPreview },
    code: {
      component: ({
        className,
        children,
        ...rest
      }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => {
        if (className === 'lang-mermaid' && typeof children === 'string') {
          return <MermaidDiagram chart={children} />;
        }
        return (
          <code className={className} {...rest}>
            {children}
          </code>
        );
      },
    },
  },
};

// Shared markdown typography + spacing for section bodies.
const sectionMarkdownSx: SxProps<Theme> = {
  '& p': { margin: 0, mb: 0.5, '&:last-child': { mb: 0 } },
  '& ul, & ol': { mt: 0, mb: 0.5, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& code': { fontSize: '0.8rem', bgcolor: 'action.hover', px: 0.5, py: 0.25, borderRadius: 0.5 },
  '& pre': {
    bgcolor: 'action.hover',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1, mb: 0.5, fontWeight: 600 },
  '& h1': { fontSize: '1.1rem' },
  '& h2': { fontSize: '1rem' },
  '& h3, & h4, & h5, & h6': { fontSize: '0.9rem' },
  '& table': { borderCollapse: 'collapse', mb: 1, fontSize: '0.85rem', width: '100%' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 },
  '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(190, 98, 165, 0.4)',
    '&:hover': { textDecorationColor: 'primary.main' },
  },
  fontSize: '0.875rem',
  lineHeight: 1.7,
  wordBreak: 'break-word',
  color: 'text.primary',
  cursor: 'text',
  minHeight: 40,
};

interface Props {
  section: ReportSection;
  studyId?: string;
  sectionNumber: number;
  isFirst: boolean;
  isLast: boolean;
  onTitleSave: (title: string) => void;
  onContentSave: (content: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onRemoveChart: (chartId: string) => void;
  onRemoveTable: (tableId: string) => void;
}

// Presentational/editor card for a single report section.
// Keeps title/content editing local while delegating persistence to parent callbacks.
const StudyReportSectionCard: React.FC<Props> = ({
  section,
  studyId,
  sectionNumber,
  isFirst,
  isLast,
  onTitleSave,
  onContentSave,
  onMoveUp,
  onMoveDown,
  onDelete,
  onRemoveChart,
  onRemoveTable,
}) => {
  const [contentDraft, setContentDraft] = React.useState(section.content || '');
  const [isEditing, setIsEditing] = React.useState(false);
  const textFieldRef = React.useRef<HTMLTextAreaElement>(null);
  const resolvedContentDraft = useEntityMergeTagResolver({ content: contentDraft, studyId });

  // Refresh draft whenever backend content changes (e.g. after mutation/query refresh).
  React.useEffect(() => {
    setContentDraft(section.content || '');
  }, [section.content]);

  // Move focus into the editor as soon as edit mode starts.
  React.useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEditing]);

  const hasCharts = section.charts.length > 0;
  const hasTables = section.tables.length > 0;

  const clipboard = useClipboard({ copiedTimeout: 1500 });
  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      clipboard.copy(contentDraft);
    },
    [clipboard, contentDraft]
  );

  return (
    <StudyReportSectionSurface
      sectionNumber={sectionNumber}
      title={
        <CardContentEditable
          disabled={false}
          savedValue={section.title}
          onSave={onTitleSave}
          onEditModeChange={() => undefined}
          placeholder="Section title"
          style={{ width: '100%' }}
        />
      }
      actions={
        <>
          <IconButton size="small" disabled={isFirst} onClick={onMoveUp}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={isLast} onClick={onMoveDown}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </>
      }
    >
      <>
        {hasCharts && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 2,
              minWidth: 0,
              ...(section.charts.length > 1
                ? {
                    '@media (min-width:821px)': {
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    },
                  }
                : {}),
            }}
          >
            {map(section.charts, (chart) => (
              <Box key={chart.id} sx={{ minWidth: 0 }}>
                <StudyReportSectionChartCard
                  chart={chart}
                  studyId={studyId}
                  sectionNumber={sectionNumber}
                  onRemove={() => onRemoveChart(chart.id)}
                />
              </Box>
            ))}
          </Box>
        )}

        {hasTables &&
          map(section.tables, (table) => (
            <StudyReportSectionTableCard
              key={table.id}
              table={table}
              studyId={studyId}
              onRemove={() => onRemoveTable(table.id)}
            />
          ))}

        {isEditing ? (
          <TextField
            inputRef={textFieldRef}
            multiline
            minRows={hasCharts || hasTables ? 2 : 4}
            label="Section content (markdown supported)"
            value={contentDraft}
            onChange={(event) => setContentDraft(event.target.value)}
            onBlur={() => {
              // Persist only when value changed to avoid noisy no-op updates.
              setIsEditing(false);
              if (contentDraft !== (section.content || '')) {
                onContentSave(contentDraft);
              }
            }}
            placeholder="Analysis notes, narrative, or observations... (markdown supported)"
            size="small"
          />
        ) : contentDraft ? (
          <Box sx={{ position: 'relative', '&:hover .copy-section-btn': { opacity: 1 } }}>
            <Tooltip title={clipboard.copied ? 'Copied!' : 'Copy section text'}>
              <IconButton
                className="copy-section-btn"
                size="small"
                onClick={handleCopy}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  opacity: 0,
                  transition: 'opacity 0.15s',
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {clipboard.copied ? (
                  <DoneIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Box
              onClick={() => setIsEditing(true)}
              sx={{
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                minHeight: hasCharts || hasTables ? 56 : 100,
                '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
                ...sectionMarkdownSx,
              }}
            >
              <Markdown options={sectionMarkdownOptions}>{resolvedContentDraft}</Markdown>
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => setIsEditing(true)}
            sx={{
              cursor: 'pointer',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              minHeight: hasCharts || hasTables ? 56 : 100,
              display: 'flex',
              alignItems: 'center',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Click to add content (markdown supported)...
            </Typography>
          </Box>
        )}
      </>
    </StudyReportSectionSurface>
  );
};

export default StudyReportSectionCard;
